import { Stack, Typography } from '@mui/material';
import { IMedicalReferral } from 'API/medicalReferrals';
import { AdvancedSearchFilteredCategories } from 'Components/AdvancedSearchFilteredCategories';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useContext, useEffect, useState } from 'react';
import { AdvancedSearchContext } from 'Context/MedicalReferralsContext/AdvancedSearchProvider';
import { getMedicalReferralStatus } from 'Utils/getMedicalReferralStatus';
import { format } from 'date-fns';
import { NoResults2 } from 'Components';
import { ReferralAccordion } from 'Modules/MedicalReferrals/ReferralAccordion';

interface ISearchCategories {
  value?: string | number;
  multiChoiceValue?: {
    value: string;
    searchValueID: number;
  }[];
  searchValueID: number;
}

interface IFilteredReferences {
  filteredReferrals: IMedicalReferral[];
  dispatchReferrals: Function;
}

export const FilteredReferences = ({
  filteredReferrals,
  dispatchReferrals,
}: IFilteredReferences) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicalReferrals)
    .search.advancedSearch;
  const [searchCategories, setSearchCategories] = useState<ISearchCategories[]>(
    []
  );
  const { advancedSearch, dispatchAdvancedSearch } = useContext(
    AdvancedSearchContext
  );
  const isDesktop = useIsDesktop();

  const resultNumber = filteredReferrals.length;

  const maxSingleSearchFieldsID = 4; // The max ID search field where its singular const and not an array.

  useEffect(() => {
    if (advancedSearch.activeSearch.text) return;
    const categories: ISearchCategories[] = [];
    const {
      requestState,
      requestNumber,
      providerName,
      dateRange,
      requestValidation,
    } = advancedSearch;

    if (requestState !== null)
      categories.push({
        value: getMedicalReferralStatus(requestState, dictionary.options, true) // Static true until ENUM hotfix Status code for canceled.
          .text,
        searchValueID: 1,
      });
    if (requestNumber !== null)
      categories.push({
        value: requestNumber,
        searchValueID: 2,
      });
    if (providerName !== null)
      categories.push({
        value: providerName,
        searchValueID: 3,
      });
    if (dateRange.start || dateRange.end) {
      const dateChoices = [];
      if (dateRange.start)
        dateChoices.push({
          value:
            dictionary.fromDate +
            ' ' +
            format(new Date(dateRange.start), 'dd/MM/yyyy'),
          searchValueID: 1,
        });
      if (dateRange.end)
        dateChoices.push({
          value:
            dictionary.toDate +
            ' ' +
            format(new Date(dateRange.end), 'dd/MM/yyyy'),
          searchValueID: 2,
        });
      categories.push({
        multiChoiceValue: dateChoices,
        searchValueID: 4,
      });
    }
    if (
      requestValidation.futureValidated ||
      requestValidation.pastValidated ||
      requestValidation.presentValidated
    ) {
      const requestChoices = [];
      if (requestValidation.pastValidated)
        requestChoices.push({
          value: dictionary.options.invalid,
          searchValueID: 1,
        });
      if (requestValidation.presentValidated)
        requestChoices.push({
          value: dictionary.options.valid,
          searchValueID: 2,
        });
      if (requestValidation.futureValidated)
        requestChoices.push({
          value: dictionary.options.future,
          searchValueID: 3,
        });
      categories.push({
        multiChoiceValue: requestChoices,
        searchValueID: 5,
      });
    }
    if (categories.length === 0) {
      dispatchReferrals({ type: 'RESET' });
      dispatchAdvancedSearch({ type: 'RESET' });
    }
    setSearchCategories(categories);
    dispatchReferrals({
      type: 'ADVANCED_SEARCH',
      payload: { value: advancedSearch },
    });
  }, [advancedSearch, dictionary, dispatchAdvancedSearch, dispatchReferrals]);

  const removeSearchCategoryHandler = (
    event: any,
    searchValueID: number | undefined,
    subValueFieldID: number | undefined
  ) => {
    dispatchAdvancedSearch({
      type: 'REMOVE_SEARCH_OPTION',
      payload: { searchValueID, subValueFieldID },
    });
  };

  return (
    <Stack direction='column' margin='0 0 50px 0' gap='15px'>
      {advancedSearch.activeSearch.advanced && (
        <AdvancedSearchFilteredCategories
          resultNumber={resultNumber}
          searchCategories={searchCategories}
          removeSearchCategory={removeSearchCategoryHandler}
          maxSingleSearchFieldsID={maxSingleSearchFieldsID}
        />
      )}
      {resultNumber > 0 && advancedSearch.activeSearch.text && (
        <Typography fontWeight={400} fontSize='16px'>
          {Dictionary[DICTIONARY.MedicalReferrals].result.found}
          <strong> {resultNumber} </strong>
          {Dictionary[DICTIONARY.MedicalReferrals].result.results}
          {!isDesktop && (
            <> {Dictionary[DICTIONARY.MedicalReferrals].result.chosenFilter}</>
          )}
        </Typography>
      )}
      {resultNumber > 0 &&
        filteredReferrals.map((referral, index) => {
          return <ReferralAccordion referral={referral} key={index} />;
        })}
      {advancedSearch.activeSearch.text && resultNumber === 0 && <NoResults2 />}
    </Stack>
  );
};
